import { useId as reachUseId } from '@reach/auto-id';

// @ts-ignore
// this stops the useLayoutEffect in SSR warning
export const useId: typeof reachUseId =
  typeof window === 'undefined'
    ? (fallback?: string) => {
        return fallback;
      }
    : reachUseId;

/**
 * Joins strings to format IDs for compound components.
 *
 * @param args
 */
export function makeId(...args: (string | number | null | undefined)[]) {
  return args
    .filter((val) => {
      return val != null;
    })
    .join('--');
}

type IdFactoryBase = {
  base: typeof makeId;
};
type IdFactoryFields<F extends string> = {
  [K in F]: typeof makeId;
};
type IdFactory<F extends string> = IdFactoryBase & IdFactoryFields<F>;
/**
 * Creates a factory for generating IDs based on a base string and a set of fields.
 *
 * @example
 *
 * const factory = makeIdFactory('my-component', ['button', 'input', 'item']);
 *
 * // Usage:
 *
 * <div data-testid={factory.base()}> // my-component
 *   {things.map((thing) => (
 *     <div key={thing.id} data-testid={factory.item(thing.id)}>
 *       {thing.name}
 *       <button>some button</button>
 *     </div>
 *  ))}
 * </div>
 *
 * // in QA tests
 *
 * // public composition
 * //*[data-testid="my-component-item--123"]//*[data-testid="other-component-button"]
 *
 * // private composition
 * //*[data-testid="my-component-item--123--other-component-button"]
 *
 */
export function makeIdFactory<T extends string, F extends string>(
  base: T,
  fields: F[] = []
) {
  const makeFieldId = (...args: (string | undefined)[]) => {
    return args.filter(Boolean).join('-');
  };

  return function makeFields(id?: string) {
    const baseId = id || base;
    const output = fields.reduce(
      (result, fieldName) => {
        return {
          ...result,
          [fieldName]: (
            id: string,
            ...args: (string | number | null | undefined)[]
          ) => {
            return makeId(makeFieldId(baseId, fieldName), id, ...args);
          },
        };
      },
      {
        base: (id: string, ...args: Parameters<typeof makeId>) => {
          return makeId(baseId, id, ...args);
        },
      } as IdFactoryBase
    ) as IdFactory<F>;
    return output;
  };
}
